import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { PageTitle } from "./AdminAnalytics";
import { ShopifySection } from "./ShopifySection";
import { WhatsAppCloudSection } from "./WhatsAppCloudSection";

const EMPTY = { shop_domain: "", cart_mode: "cart", admin_emails: "", whatsapp_number: "", chat_enabled: true, notify_email: "", email_from: "", email_reply_to: "", followup_enabled: true, weekly_report_enabled: true, weekly_report_email: "",
  catalog_excluded_collections: "", catalog_addon_collections: "", catalog_eye_collection: "", catalog_eye_patch_collection: "", alternative_only_brands: "", followup_days: 3, shopify_webhook_secret: "", shopify_shop: "", shopify_client_id: "", shopify_client_secret: "" };

const Field = ({ k, label, hint, placeholder, form, set }) => (
  <div>
    <Label>{label}</Label>
    <Input data-testid={`settings-${k.replace(/_/g, "-")}-input`} className="mt-2" value={form[k]} onChange={(e) => set(k)(e.target.value)} placeholder={placeholder} />
    {hint && <p className="text-xs text-[#8C8275] mt-2">{hint}</p>}
  </div>
);

export default function AdminSettings() {
  const [form, setForm] = useState(EMPTY);
  const [testTo, setTestTo] = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const hydrate = useCallback((data) => setForm({ ...data, admin_emails: data.admin_emails.join("\n") }), []);
  const fp = { form, set };
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/shopify/webhooks/orders`;

  useEffect(() => { api.get("/admin/settings").then(({ data }) => hydrate(data)); }, [hydrate]);

  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings", { ...form, admin_emails: form.admin_emails.split(/[\n,]/) });
      hydrate(data);
      toast.success("Impostazioni salvate");
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };

  const reset = async () => {
    if (!window.confirm("Ripristinare tutti i dati demo? Domande, tag, profili, routine e prodotti verranno sostituiti.")) return;
    await api.post("/admin/reset-demo");
    toast.success("Dati demo ripristinati");
  };

  const emailTest = async () => {
    try {
      if (!(await save())) return;
      await api.post("/admin/email/test", { to: testTo });
      toast.success(`Routine di prova inviata a ${testTo}: controlla la posta`);
      const { data } = await api.get("/admin/settings");
      hydrate(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendReport = async () => {
    try {
      if (!(await save())) return;
      const { data } = await api.post("/admin/analytics/weekly-report/send");
      toast.success(`Report inviato a ${data.to}: controlla la posta`);
      hydrate((await api.get("/admin/settings")).data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-settings-page" className="max-w-2xl">
      <PageTitle eyebrow="Configurazione" title="Impostazioni" />
      <div className="bg-white border border-[#E2DACF] p-6 space-y-6">
        <Field {...fp} k="shop_domain" label="Dominio Shopify" placeholder="fineskinbeauty.com"
          hint={<>I prodotti della routine vengono aggiunti al carrello di <code>https://{form.shop_domain || "dominio"}</code> con i parametri UTM del cliente. Lo stesso link è usato nel PDF e nel messaggio WhatsApp/email.</>} />
        <div className="border border-[#E6DFD5] p-4 space-y-4" data-testid="settings-catalog-rules">
          <div>
            <p className="font-medium text-sm">Regole catalogo (applicate a ogni sincronizzazione)</p>
            <p className="text-xs text-[#8C8275] mt-1">Usa gli handle delle collezioni Shopify (la parte finale dell'URL della collezione, es. <code>hair-care</code>), separati da virgola. Puoi sempre correggere il singolo prodotto dal suo ruolo in Catalogo → Prodotti.</p>
          </div>
          <Field {...fp} k="catalog_excluded_collections" label="Collezioni escluse dalle routine" placeholder="hair-care" hint="Es. Haircare: questi prodotti non compaiono mai (riconosciuti anche da tag #haircare, shampoo, scalp)." />
          <Field {...fp} k="catalog_addon_collections" label="Collezioni proposte come “in più” (device)" placeholder="beauty-device" hint="Mai nella routine: appaiono nel tasto “Potenzia con un device” accanto allo step indicato per ciascun prodotto (default: Trattamento)." />
          <Field {...fp} k="catalog_eye_collection" label="Collezione contorno occhi (Eye Care)" placeholder="contorno-occhi" hint="Lo step Contorno occhi usa solo prodotti di questa collezione." />
          <Field {...fp} k="catalog_eye_patch_collection" label="Collezione patch occhi" placeholder="patch-viso-e-occhi" hint="Solo i patch occhi di questa collezione vengono proposti nel tasto “Patch occhi” accanto allo step Contorno occhi." />
          <Field {...fp} k="alternative_only_brands" label="Brand solo come alternativa" placeholder="Medicube, VT Cosmetics" hint="Mai prima scelta: compaiono tra le alternative compatibili (a meno che non ci sia nient'altro per quello step)." />
        </div>
        <div>
          <Label>Dove porta "Acquista la mia routine"</Label>
          <div className="mt-2 inline-flex border border-[#E2DACF] p-1" role="radiogroup">
            {[["cart", "Carrello Shopify"], ["checkout", "Checkout diretto"]].map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={form.cart_mode === k} data-testid={`settings-cart-mode-${k}`} onClick={() => set("cart_mode")(k)}
                className={`px-4 py-2 text-xs tracking-[0.12em] uppercase transition-colors ${form.cart_mode === k ? "bg-[#1A1A18] text-[#FAF8F5]" : "text-[#595652] hover:text-[#1A1A18]"}`}>{l}</button>
            ))}
          </div>
          <p className="text-xs text-[#8C8275] mt-2">{form.cart_mode === "cart" ? "I prodotti vengono aggiunti al carrello del negozio e il cliente vede la pagina carrello (può modificare quantità e continuare lo shopping)." : "Il cliente arriva subito al checkout con i prodotti già inseriti."}</p>
        </div>
        <div>
          <Label>Email amministratori (una per riga)</Label>
          <Textarea data-testid="settings-admin-emails-input" className="mt-2" rows={4} value={form.admin_emails} onChange={(e) => set("admin_emails")(e.target.value)} />
        </div>
        <button data-testid="settings-save-btn" className="fs-btn" onClick={save}>Salva</button>
      </div>

      <div className="bg-white border border-[#E2DACF] p-6 mt-6 space-y-6">
        <p className="font-serif text-xl">Chat live e notifiche</p>
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label>Chat attiva sul sito</Label>
            <p className="text-xs text-[#8C8275] mt-1">Mostra il bottone chat ai clienti su tutte le pagine.</p>
          </div>
          <Switch data-testid="settings-chat-enabled-switch" checked={form.chat_enabled} onCheckedChange={set("chat_enabled")} />
        </div>
        <Field {...fp} k="notify_email" label="Email per gli avvisi chat" placeholder="tu@esempio.com"
          hint="Ricevi una email ad ogni nuovo messaggio di un cliente, con il link per rispondere." />
        <Field {...fp} k="email_from" label="Mittente email (Resend)" placeholder="FineSkin Beauty <routine@fineskinbeauty.com>"
          hint="Dominio fineskinbeauty.com verificato su Resend: usa un indirizzo di quel dominio. Da qui partono routine, avvisi chat e follow-up." />
        <Field {...fp} k="email_reply_to" label="Indirizzo per le risposte (Reply-To)" placeholder="info@fineskinbeauty.com"
          hint="Quando la cliente risponde all'email della routine o del follow-up, la risposta arriva qui." />
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <Label>Invia una routine di prova via email a</Label>
            <Input data-testid="email-test-to-input" className="mt-2" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="nome@esempio.it" />
          </div>
          <button data-testid="email-test-send-btn" className="fs-btn-ghost" onClick={emailTest} disabled={!testTo}><Send size={14} /> Invia prova</button>
        </div>
        <Field {...fp} k="whatsapp_number" label="Numero WhatsApp FineSkin" placeholder="+39..."
          hint="Mostrato nella chat come contatto alternativo." />
        {form.last_email_status && (
          <div data-testid="settings-email-status" className={`text-xs p-3 border ${form.last_email_status.ok ? "border-[#CFE3D3] bg-[#F1F8F2] text-[#2F6B3A]" : "border-[#EBD3C9] bg-[#FBF3EF] text-[#A0482F]"}`}>
            <p className="font-medium">Ultimo avviso email → {form.last_email_status.to} · {form.last_email_status.ok ? "inviato" : "non inviato"}</p>
            {form.last_email_status.error && <p className="mt-1">{form.last_email_status.error}</p>}
          </div>
        )}
        <button data-testid="settings-save-chat-btn" className="fs-btn" onClick={save}>Salva</button>
      </div>

      <div className="bg-white border border-[#E2DACF] p-6 mt-6 space-y-6">
        <p className="font-serif text-xl">Promemoria follow-up</p>
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label>Promemoria automatico via email</Label>
            <p className="text-xs text-[#8C8275] mt-1">Ogni giorno alle 10:00 inviamo un'email ai clienti che hanno lasciato l'indirizzo e non hanno ancora acquistato. Per WhatsApp usa il pulsante nella sezione Clienti → "Da ricontattare".</p>
          </div>
          <Switch data-testid="settings-followup-enabled-switch" checked={form.followup_enabled} onCheckedChange={set("followup_enabled")} />
        </div>
        <div>
          <Label>Dopo quanti giorni</Label>
          <Input data-testid="settings-followup-days-input" type="number" min={1} max={30} className="mt-2 w-32" value={form.followup_days}
            onChange={(e) => set("followup_days")(Math.max(1, parseInt(e.target.value || "1", 10)))} />
          <p className="text-xs text-[#8C8275] mt-2">Un cliente è considerato "ha acquistato" se ha cliccato "Acquista la mia routine" o se lo segni tu nella sezione Clienti.</p>
        </div>
        <button data-testid="settings-save-followup-btn" className="fs-btn" onClick={save}>Salva</button>
      </div>

      <div className="bg-white border border-[#E2DACF] p-6 mt-6 space-y-6" data-testid="settings-weekly-report-section">
        <p className="font-serif text-xl">Report settimanale</p>
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label>Email automatica ogni lunedì</Label>
            <p className="text-xs text-[#8C8275] mt-1">Alle 08:00 ricevi il funnel della settimana (visite, test, routine, carrello), il confronto con la settimana precedente, nuove clienti, fasce orarie e prodotti più scelti.</p>
          </div>
          <Switch data-testid="settings-weekly-report-enabled-switch" checked={!!form.weekly_report_enabled} onCheckedChange={set("weekly_report_enabled")} />
        </div>
        <Field {...fp} k="weekly_report_email" label="Invia il report a" placeholder="info@fineskinbeauty.com" />
        {form.last_weekly_report && (
          <div data-testid="settings-weekly-report-status" className={`text-xs p-3 border ${form.last_weekly_report.ok ? "border-[#CFE3D3] bg-[#F1F8F2] text-[#2F6B3A]" : "border-[#EBD3C9] bg-[#FBF3EF] text-[#A0482F]"}`}>
            <p className="font-medium">Ultimo report → {form.last_weekly_report.to} · {form.last_weekly_report.ok ? "inviato" : "non inviato"} · {new Date(form.last_weekly_report.ts).toLocaleString("it-IT")}</p>
            {form.last_weekly_report.error && <p className="mt-1">{form.last_weekly_report.error}</p>}
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          <button data-testid="settings-save-weekly-report-btn" className="fs-btn" onClick={save}>Salva</button>
          <button data-testid="weekly-report-send-btn" className="fs-btn-ghost" onClick={sendReport} disabled={!form.weekly_report_email}><Send size={14} /> Invia ora</button>
          <a data-testid="weekly-report-preview-link" className="fs-btn-ghost" href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/weekly-report/preview`} target="_blank" rel="noreferrer">Anteprima</a>
        </div>
      </div>

      <div className="bg-white border border-[#E2DACF] p-6 mt-6 space-y-6" data-testid="settings-shopify-section">
        <div>
          <p className="font-serif text-xl">Ordini Shopify</p>
          <p className="text-sm text-[#8C8275] mt-2">Quando un ordine viene pagato, Shopify avvisa l'app e il cliente passa automaticamente ad "Acquistato".</p>
        </div>
        <ol className="text-sm text-[#595652] space-y-3 list-decimal pl-5 leading-relaxed">
          <li>Su Shopify vai in <strong>Impostazioni → Notifiche → Webhook</strong> e clicca <strong>Crea webhook</strong>.</li>
          <li>Evento <strong>Pagamento ordine</strong>, formato <strong>JSON</strong>, versione API più recente.</li>
          <li>Incolla questo URL:
            <div className="flex gap-2 mt-2">
              <code data-testid="settings-webhook-url" className="flex-1 text-xs bg-[#FAF8F5] border border-[#E6DFD5] px-3 py-2 break-all">{webhookUrl}</code>
              <button type="button" data-testid="settings-copy-webhook-url-btn" className="fs-btn-ghost !py-2 !px-3 text-xs" onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("URL copiato"); }}>Copia</button>
            </div>
          </li>
          <li>Dopo il salvataggio, in fondo alla sezione Webhook Shopify mostra <em>"I tuoi webhook saranno firmati con …"</em>: copia quel codice e incollalo qui sotto.</li>
        </ol>
        <Field {...fp} k="shopify_webhook_secret" label="Codice segreto firma webhook" placeholder="es. 7a1f3c…"
          hint="Serve a verificare che le notifiche arrivino davvero da Shopify. Viene mostrato mascherato dopo il salvataggio." />
        {form.last_shopify_webhook && (
          <div data-testid="settings-shopify-status" className={`text-xs p-3 border ${form.last_shopify_webhook.ok ? "border-[#CFE3D3] bg-[#F1F8F2] text-[#2F6B3A]" : "border-[#EBD3C9] bg-[#FBF3EF] text-[#A0482F]"}`}>
            <p className="font-medium">Ultima notifica Shopify · {new Date(form.last_shopify_webhook.ts).toLocaleString("it-IT")} · {form.last_shopify_webhook.ok ? "ricevuta" : "rifiutata"}</p>
            <p className="mt-1">{form.last_shopify_webhook.error || (form.last_shopify_webhook.matched ? `Ordine ${form.last_shopify_webhook.order_name} collegato a ${form.last_shopify_webhook.matched}` : `Ordine ${form.last_shopify_webhook.order_name || ""} ricevuto, nessun cliente corrispondente`)}</p>
          </div>
        )}
        <button data-testid="settings-save-shopify-btn" className="fs-btn" onClick={save}>Salva</button>
      </div>

      <ShopifySection form={form} set={set} save={save} />
      <details className="mt-6" data-testid="settings-wa-cloud-details">
        <summary className="cursor-pointer text-sm text-[#8C8275]">Facoltativo · WhatsApp aziendale via Meta Cloud API (non attivo: oggi la routine si invia dalla chat WhatsApp della cliente)</summary>
        <WhatsAppCloudSection form={form} set={set} save={save} />
      </details>

      <div className="bg-white border border-[#E2DACF] p-6 mt-6">
        <p className="font-serif text-xl">Dati demo</p>
        <p className="text-sm text-[#8C8275] mt-2">Ripristina il catalogo e le regole di esempio. Utile per test; i dati reali verranno sovrascritti.</p>
        <button data-testid="settings-reset-demo-btn" className="fs-btn-ghost mt-4 text-[#A0482F]" onClick={reset}>Ripristina dati demo</button>
      </div>
    </div>
  );
}
